// AI-CONTEXT: See docs/features/output-measurement-unification.md
// The "TOC rule" for how an Output is measured (ADR-011). One method per Output.
// The available set is module-gated (see allowedMeasurementMethods).

export const measurementMethods = [
  'event_form',
  'persona_attendance',
  'address_visit',
]

export const DEFAULT_MEASUREMENT_METHOD = 'event_form'

export const measurementMethodLabels = {
  event_form: 'Via formulier',
  persona_attendance: 'Via aanwezigheid (rooster)',
  address_visit: 'Via huisbezoek',
}

/**
 * The methods an organization may pick for an Output, gated by enabled modules.
 * `event_form` (via het formulier — handmatig invoeren) is altijd beschikbaar;
 * `persona_attendance` needs the persona module (`personasOn === 'flex'`);
 * `address_visit` needs huisbezoeken (`housevisits === true`). Returned in
 * canonical `measurementMethods` order.
 *
 * @param {{ personasOn?: string, housevisits?: boolean }} [settings]
 * @returns {string[]}
 */
export const allowedMeasurementMethods = ({ personasOn, housevisits } = {}) => {
  const gated = new Set(['event_form'])
  if (personasOn === 'flex') gated.add('persona_attendance')
  if (housevisits === true) gated.add('address_visit')
  return measurementMethods.filter((method) => gated.has(method))
}

/**
 * How an Output is measured; a missing `MeasurementMethod` means event_form.
 *
 * @param {{ MeasurementMethod?: string } | undefined} output
 * @returns {string}
 */
export const measurementMethodOf = (output) => output?.MeasurementMethod || DEFAULT_MEASUREMENT_METHOD

// AI-CONTEXT: See docs/features/output-registratie-per-keer-of-totaal.md (ADR-070)
// What one registration of an Output stands for: one occurrence, or a total
// over a period (dated on the last day of that period, by convention). Only
// meaningful for event_form outputs — the other methods register per occurrence.
// Display-only: no count or date filter reads it.

export const registrationTypes = ['per_occurrence', 'period_total']

export const DEFAULT_REGISTRATION_TYPE = 'per_occurrence'

export const registrationTypeLabels = {
  per_occurrence: 'Per keer',
  period_total: 'Totaal over een periode',
}

/**
 * The effective registration type of an Output. Missing or unknown values, and
 * outputs not measured via the form, are per_occurrence.
 *
 * @param {{ MeasurementMethod?: string, RegistrationType?: string } | undefined} output
 * @returns {'per_occurrence' | 'period_total'}
 */
export const registrationTypeOf = (output) => {
  if (measurementMethodOf(output) !== 'event_form') return DEFAULT_REGISTRATION_TYPE
  return output?.RegistrationType === 'period_total' ? 'period_total' : DEFAULT_REGISTRATION_TYPE
}

export default measurementMethods
